import type {
  OfferAttachment,
  OfferAttachmentMimeType,
  OfferDocumentContentBlock,
  OfferDocumentEditablePara,
  OfferDocumentResolvedContentBlock,
  OfferIntroBlock,
  OfferIntroBlockPersisted,
} from '../../../types';
import { INLINE_DOCUMENT_IMAGE_MAX_COUNT } from '../../../types';

export { INLINE_DOCUMENT_IMAGE_MAX_COUNT };

const ALLOWED_INLINE_MIMES: ReadonlyArray<OfferAttachmentMimeType> = ['image/png', 'image/jpeg'];

export function newDocumentContentId(): string {
  return `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export function isInlineImageAttachment(attachment: { mime_type: string }): boolean {
  return attachment.mime_type === 'image/png' || attachment.mime_type === 'image/jpeg';
}

function isAttachment(x: unknown): x is OfferAttachment {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id) return false;
  if (typeof o.filename !== 'string' || !o.filename) return false;
  if (typeof o.object_path !== 'string' || !o.object_path) return false;
  if (typeof o.size !== 'number' || !Number.isFinite(o.size)) return false;
  if (typeof o.mime_type !== 'string') return false;
  return ALLOWED_INLINE_MIMES.includes(o.mime_type as OfferAttachmentMimeType);
}

export function isOfferDocumentContentBlock(x: unknown): x is OfferDocumentContentBlock {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id) return false;
  if (o.type === 'text' || o.type === 'title') return typeof o.content === 'string';
  if (o.type === 'imageGrid') {
    return Array.isArray(o.images) && o.images.every(isAttachment) && o.images.length > 0;
  }
  return false;
}

export function parasToTextBlocks(paras: OfferDocumentEditablePara[] | undefined): OfferDocumentContentBlock[] {
  return (paras ?? []).map((p) => ({ id: p.id, type: 'text' as const, content: p.text }));
}

export function contentToParas(blocks: OfferDocumentContentBlock[]): OfferDocumentEditablePara[] {
  return blocks
    .filter((b): b is Extract<OfferDocumentContentBlock, { type: 'text' }> => b.type === 'text')
    .map((b) => ({ id: b.id, text: b.content }));
}

export function contentToLegacyBlocks(blocks: OfferDocumentContentBlock[]): OfferIntroBlockPersisted[] {
  const out: OfferIntroBlockPersisted[] = [];
  for (const b of blocks) {
    if (b.type === 'title') out.push({ id: b.id, type: 'title', content: b.content });
    if (b.type === 'imageGrid') {
      for (const img of b.images) out.push({ id: img.id, type: 'image', alt: img.filename });
    }
  }
  return out;
}

function legacyBlocksToContent(
  blocks: Array<OfferIntroBlock | OfferIntroBlockPersisted> | undefined,
): OfferDocumentContentBlock[] {
  const out: OfferDocumentContentBlock[] = [];
  for (const b of blocks ?? []) {
    if (b.type === 'text') out.push({ id: b.id, type: 'text', content: b.content });
    if (b.type === 'title') out.push({ id: b.id, type: 'title', content: b.content });
  }
  return out;
}

export function normalizeOfferDocumentContent(input: {
  content?: OfferDocumentContentBlock[] | null;
  paras?: OfferDocumentEditablePara[] | null;
  blocks?: Array<OfferIntroBlock | OfferIntroBlockPersisted> | null;
}): OfferDocumentContentBlock[] {
  if (Array.isArray(input.content) && input.content.every(isOfferDocumentContentBlock)) {
    return clampInlineImagesInBlocks(input.content);
  }
  return clampInlineImagesInBlocks([
    ...parasToTextBlocks(input.paras ?? undefined),
    ...legacyBlocksToContent(input.blocks ?? undefined),
  ]);
}

export function countInlineImages(blocks: OfferDocumentContentBlock[]): number {
  return blocks.reduce((sum, block) => (block.type === 'imageGrid' ? sum + block.images.length : sum), 0);
}

export function remainingInlineImageSlots(
  intro: OfferDocumentContentBlock[],
  closing: OfferDocumentContentBlock[],
): number {
  return Math.max(0, INLINE_DOCUMENT_IMAGE_MAX_COUNT - countInlineImages(intro) - countInlineImages(closing));
}

export function collectInlineImageAttachments(blocks: OfferDocumentContentBlock[]): OfferAttachment[] {
  return blocks.flatMap((block) => (block.type === 'imageGrid' ? block.images : []));
}

export function clampInlineImagesAcrossZones(
  intro: OfferDocumentContentBlock[],
  closing: OfferDocumentContentBlock[],
  max = INLINE_DOCUMENT_IMAGE_MAX_COUNT,
): { intro: OfferDocumentContentBlock[]; closing: OfferDocumentContentBlock[] } {
  const nextIntro = clampInlineImagesInBlocks(intro, max);
  const remaining = Math.max(0, max - countInlineImages(nextIntro));
  return {
    intro: nextIntro,
    closing: clampInlineImagesInBlocks(closing, remaining),
  };
}

export function clampInlineImagesInBlocks(
  blocks: OfferDocumentContentBlock[],
  max = INLINE_DOCUMENT_IMAGE_MAX_COUNT,
): OfferDocumentContentBlock[] {
  let remaining = max;
  const out: OfferDocumentContentBlock[] = [];
  for (const block of blocks) {
    if (block.type !== 'imageGrid') {
      out.push(block);
      continue;
    }
    if (remaining <= 0) continue;
    const images = block.images.filter(isInlineImageAttachment).slice(0, remaining);
    remaining -= images.length;
    if (images.length > 0) out.push({ ...block, images });
  }
  return out;
}

export function insertContentBlock(
  blocks: OfferDocumentContentBlock[],
  index: number,
  block: OfferDocumentContentBlock,
): OfferDocumentContentBlock[] {
  const next = [...blocks];
  const at = Math.max(0, Math.min(index, next.length));
  next.splice(at, 0, block);
  return next;
}

export function updateContentText(
  blocks: OfferDocumentContentBlock[],
  id: string,
  content: string,
): OfferDocumentContentBlock[] {
  return blocks.map((block) =>
    block.id === id && (block.type === 'text' || block.type === 'title') ? { ...block, content } : block,
  );
}

export function removeContentBlock(
  blocks: OfferDocumentContentBlock[],
  id: string,
): OfferDocumentContentBlock[] {
  return blocks.filter((block) => block.id !== id);
}

export function findImageGridBlockId(
  blocks: OfferDocumentContentBlock[],
  attachmentId: string,
): string | null {
  const block = blocks.find(
    (item) => item.type === 'imageGrid' && item.images.some((image) => image.id === attachmentId),
  );
  return block?.type === 'imageGrid' ? block.id : null;
}

export function replaceGridImages(
  blocks: OfferDocumentContentBlock[],
  blockId: string,
  images: OfferAttachment[],
): OfferDocumentContentBlock[] {
  const nextImages = images.filter(isInlineImageAttachment);
  if (nextImages.length === 0) return removeContentBlock(blocks, blockId);
  return blocks.map((block) =>
    block.id === blockId && block.type === 'imageGrid' ? { ...block, images: nextImages } : block,
  );
}

export function replaceAttachmentInContent(
  blocks: OfferDocumentContentBlock[],
  attachmentId: string,
  nextAttachment: OfferAttachment,
): OfferDocumentContentBlock[] {
  return blocks.map((block) => {
    if (block.type !== 'imageGrid') return block;
    const images = block.images.map((img) => (img.id === attachmentId ? nextAttachment : img));
    return { ...block, images };
  });
}

export function removeAttachmentFromContent(
  blocks: OfferDocumentContentBlock[],
  attachmentId: string,
): OfferDocumentContentBlock[] {
  const out: OfferDocumentContentBlock[] = [];
  for (const block of blocks) {
    if (block.type !== 'imageGrid') {
      out.push(block);
      continue;
    }
    const images = block.images.filter((img) => img.id !== attachmentId);
    if (images.length > 0) out.push({ ...block, images });
  }
  return out;
}

export function updateFirstTextBlock(
  blocks: OfferDocumentContentBlock[],
  text: string,
  opts?: { createIfMissing?: boolean },
): OfferDocumentContentBlock[] {
  const index = blocks.findIndex((block) => block.type === 'text');
  if (index >= 0) {
    const current = blocks[index];
    if (current.type !== 'text') return blocks;
    const next = [...blocks];
    next[index] = { ...current, content: text };
    return next;
  }
  if (opts?.createIfMissing === false) return blocks;
  return [{ id: newDocumentContentId(), type: 'text', content: text }, ...blocks];
}

export function replaceTextBlocksKeepingMedia(
  blocks: OfferDocumentContentBlock[] | undefined,
  texts: string[],
): OfferDocumentContentBlock[] {
  const media = (blocks ?? []).filter((block) => block.type !== 'text');
  const textBlocks = texts.map((content, i) => {
    const existing = (blocks ?? []).filter((block) => block.type === 'text')[i];
    return {
      id: existing?.id ?? newDocumentContentId(),
      type: 'text' as const,
      content,
    };
  });
  return [...textBlocks, ...media];
}

export function mapResolvedContent(
  blocks: OfferDocumentContentBlock[],
  srcById: Map<string, string>,
): {
  content: OfferDocumentResolvedContentBlock[];
  unresolved: OfferAttachment[];
} {
  const unresolved: OfferAttachment[] = [];
  const content: OfferDocumentResolvedContentBlock[] = [];
  for (const block of blocks) {
    if (block.type !== 'imageGrid') {
      content.push(block);
      continue;
    }
    const images: Array<{ id: string; src: string; alt: string }> = [];
    for (const image of block.images) {
      const src = srcById.get(image.id);
      if (src) images.push({ id: image.id, src, alt: image.filename });
      else unresolved.push(image);
    }
    if (images.length > 0) content.push({ id: block.id, type: 'imageGrid', images });
  }
  return { content, unresolved };
}
